'use client';

import { useParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import type { UseFormGetValues } from 'react-hook-form';

import { isInternalServerError } from '@payments-checkout/shared/helpers/constants/http-status.const';
import { isFailed } from '@payments-checkout/shared/helpers/constants/order-status.const';
import {
  getAttempt,
  reportDegradation,
  reportError,
  trackAttempt,
} from '@payments-checkout/shared/observability';
import { experimentInstance } from '@payments-checkout/shared/plugins/analitica/analitica.configs';
import { analiticaInstance } from '@payments-checkout/shared/plugins/analitica/analitica.events';
import { usePaymentMethod } from '@payments-checkout/shared/providers';

import { useRecurrenceValues } from '../../app/hook/useRecurrence/useRecurrenceValues.hook';
import { useFormTypedValues } from '../../app/providers/formValuesProvider.provider';
import { BillingInformationFormFields } from '../../app/types/billingInformation.types';
import {
  get3dsParam,
  getPaymentParam,
  getTokenizeCardParam,
  isCreditCard,
} from '../../components/pages/cardAddress/creditCardAddress.helpers';
import {
  errorMessagePaymentGeneric,
} from '../../components/pages/cardAddress/errorMessage';
import { AddressFields } from '../../types/address';
import { get3dsToken } from '../use3tdToken';
import { usePageSharedData } from '../usePageSharedData';
import { usePayment } from '../usePayment';
import { useSendTokenizeCard } from '../useTokenizeCard';

type AuthSwitchInitConfig = {
  publicKey: string;
  tds: {
    token: string;
    orderData: unknown;
    tdsMethodContainerElement: HTMLElement | null;
    challengeContainerElement: HTMLElement | null;
  };
};

type AuthSwitchResponse = {
  risk_id?: string;
  steps?: {
    tds?: Array<{ tds_server_trans_id?: string }>;
  };
};

declare global {
  interface Window {
    authSwitch: {
      init: (config: AuthSwitchInitConfig) => Promise<AuthSwitchResponse>;
    };
    Script3ds: {
      init3ds: (
        tdsToken: string,
        tdsParam: any,
        callback: (value: any) => void,
        version: string
      ) => void;
    };
    TDS: {
      init: (
        {
          token,
          tdsMethodContainerElement,
          challengeContainerElement,
          useDefaultChallengeIFrameStyle,
          challengeWindowSize,
        }: any,
        orderData: any
      ) => Promise<any>;
    };
  }
}

type UseCreditCardPaymentParams = {
  getValues: UseFormGetValues<BillingInformationFormFields>;
};

const AUTH_SWITCH_EXPERIMENT_KEY = 'script-auth-switch';

async function isAuthSwitchEnabled() {
  try {
    const variant = await experimentInstance.variant(
      AUTH_SWITCH_EXPERIMENT_KEY,
      'off'
    );
    return variant?.value === 'on';
  } catch (error) {
    reportDegradation('three_ds_fallback', error, {
      attempt: getAttempt('three_ds_init'),
    });
    return false;
  }
}

export function useCreditCardPayment({ getValues }: UseCreditCardPaymentParams) {
  const params = useParams<{ paymentLinkId: string }>();
  const {
    paymentMethod,
    publicKey,
    antifraudId,
    brand,
    threedSecureEnabled,
    setFailedPaymentMessage,
    setShowFailedPaymentAlert,
  } = usePaymentMethod();
  const { total, useBrandRate, isRecurrence, isDeliveryEnabled } =
    usePageSharedData();
  const { price } = useRecurrenceValues();
  const { formValues, deliveryQuote } = useFormTypedValues();

  const [tdsLoading, setTdsLoading] = useState(false);
  const [tdsToken, setTdsToken] = useState('');

  const cardTokenRef = useRef('');
  const tdsDataRef = useRef<{ tds_server_trans_id?: string } | undefined>({});
  const riskIdRef = useRef('');
  const isSubmittingRef = useRef(false);

  const {
    mutate: sendPayment,
    isPending: isPaymentPending,
    data: paymentResponse,
  } = usePayment({
    onSettled: () => {
      isSubmittingRef.current = false;
    },
    onError: (error) => {
      const errorStatus = error?.response?.status ?? 500;
      reportError(error, {
        source: 'payment_submit',
        payment_method: paymentMethod,
      });
      analiticaInstance.events.billingPage.paymentErrorViewed(
        String(errorStatus)
      );
      setShowFailedPaymentAlert(true);
      setFailedPaymentMessage(
        isInternalServerError(errorStatus)
          ? errorMessagePaymentGeneric(1002)
          : errorMessagePaymentGeneric(1000)
      );
    },
  });

  useEffect(() => {
    if (
      paymentResponse?.order_status &&
      isFailed(paymentResponse?.order_status)
    ) {
      analiticaInstance.events.billingPage.paymentErrorViewed(
        paymentResponse.order_status
      );
      setShowFailedPaymentAlert(true);
      setFailedPaymentMessage(errorMessagePaymentGeneric(1000));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentResponse?.order_status]);

  const { mutateAsync: sendTokenizeCard, isPending: isTokenizeCardPending } =
    useSendTokenizeCard({
      onSuccess: () => {
        analiticaInstance.events.billingPage.cardTokenized(true);
      },
      onError: (error) => {
        reportError(error, { source: 'tokenize_card' });
        analiticaInstance.events.billingPage.cardTokenized(false);
        analiticaInstance.events.billingPage.paymentErrorViewed(
          String(error?.response?.status ?? 500)
        );
        setShowFailedPaymentAlert(true);
        setFailedPaymentMessage(
          isInternalServerError(error?.response?.status ?? 500)
            ? errorMessagePaymentGeneric(1001)
            : errorMessagePaymentGeneric(1000)
        );
      },
    });

  function resetTdsData() {
    tdsDataRef.current = undefined;
    riskIdRef.current = '';
  }

  function pay() {
    const paymentParams = getPaymentParam(
      cardTokenRef.current,
      paymentMethod ?? 'credit_card',
      Number(getValues('installments')) ?? null,
      antifraudId,
      tdsDataRef.current,
      useBrandRate,
      brand,
      deliveryQuote?.id,
      isDeliveryEnabled,
      riskIdRef.current
    );

    trackAttempt('payment_submit');
    sendPayment({ paymentData: paymentParams.payment });
  }

  async function tokenizeCard() {
    trackAttempt('tokenize_card');

    try {
      const response: any = await sendTokenizeCard({
        publicKey,
        cardData: getTokenizeCardParam(
          getValues('creditCard') ?? null,
          getValues('cardHolder') ?? null,
          getValues('expirationDate') ?? null,
          getValues('cvv') ?? null
        ),
      });
      cardTokenRef.current = response.id;
      pay();
    } catch {
      isSubmittingRef.current = false;
    }
  }

  async function tdsCallback(value: any) {
    setTdsLoading(false);
    if (value.length > 0) {
      tdsDataRef.current = value[0];
      analiticaInstance.events.billingPage.threeDsProcessed();
    }
    await tokenizeCard();
  }

  async function authCallback(response: AuthSwitchResponse) {
    setTdsLoading(false);
    tdsDataRef.current = response?.steps?.tds && response?.steps?.tds[0];
    riskIdRef.current = response?.risk_id ?? '';
    await tokenizeCard();
  }

  async function initTds(address: { address: AddressFields }) {
    trackAttempt('three_ds_init');
    setTdsLoading(true);

    const tdsParam = get3dsParam(
      formValues?.name,
      address,
      {
        cardNumber: getValues().creditCard ?? null,
        cardHolder: getValues().cardHolder,
        expirationDate: getValues().expirationDate,
        cvv: getValues().cvv,
      },
      formValues?.phone ?? '',
      formValues?.countryCode ?? '',
      formValues?.email ?? '',
      formValues?.documentType ?? '',
      formValues?.cpf ?? formValues?.cnpj ?? '',
      isRecurrence ? { amount: price } : total
    );

    const tdsMethodContainer = document.getElementById('tdsMethodContainer');
    const challengeContainer = document.getElementById('challengeContainer');

    try {
      const [response, authSwitchEnabled] = await Promise.all([
        get3dsToken(params.paymentLinkId),
        isAuthSwitchEnabled(),
      ]);

      if (authSwitchEnabled) {
        const riskResponse = await window.authSwitch.init({
          publicKey,
          tds: {
            token: response.tds_token,
            orderData: tdsParam,
            tdsMethodContainerElement: tdsMethodContainer,
            challengeContainerElement: challengeContainer,
          },
        });

        await authCallback(riskResponse);
      } else {
        setTdsToken(response.tds_token);
        const tdsResponse = await window.TDS.init(
          {
            token: response.tds_token,
            tds_method_container_element: tdsMethodContainer,
            challenge_container_element: challengeContainer,
            use_default_challenge_iframe_style: false,
            challenge_window_size: '03',
          },
          tdsParam
        );

        const cardNumber = tdsParam.payments[0].credit_card.card.number ?? '';
        analiticaInstance.events.billingPage.threeDsTokenized(true, {
          firstSixDigits: cardNumber.slice(0, 6),
          lastFourDigits: cardNumber.slice(-4),
        });

        await tdsCallback(tdsResponse);
      }
    } catch (error) {
      reportDegradation('three_ds_fallback', error, {
        attempt: getAttempt('three_ds_init'),
      });
      analiticaInstance.events.billingPage.threeDsTokenized(false);
      setTdsLoading(false);
      resetTdsData();
      await tokenizeCard();
    }
  }

  async function submitPayment(address: { address: AddressFields }) {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    resetTdsData();

    try {
      if (!isCreditCard(paymentMethod)) return pay();
      if (threedSecureEnabled) return await initTds(address);
      return await tokenizeCard();
    } catch (error) {
      isSubmittingRef.current = false;
      throw error;
    }
  }

  return {
    submitPayment,
    isPaymentPending,
    isTokenizeCardPending,
    tdsLoading,
    tdsToken,
  };
}
